'use client';

import { useEffect, useState } from 'react';

const OFFSET = 120;
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/**
 * Returns the id of the section in view (last of `ids`, in page order, scrolled past the nav), or null.
 * Clicking an in-page link pins its id until the scroll ends or the user takes over scrolling,
 * and when scrolling ends the URL hash follows the section.
 */
export const useActiveSection = (ids: readonly string[]) => {
  const [scrolledId, setScrolledId] = useState<string | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);

  useEffect(() => {
    const detect = () =>
      ids.findLast(
        (id) => (document.getElementById(id)?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY) <= OFFSET,
      ) ?? null;
    const unpin = () => setPinnedId(null);
    const onScroll = () => setScrolledId(detect());

    const onClick = (event: MouseEvent) => {
      const link = (event.target as Element).closest('a');
      const id = link?.hash.slice(1);
      if (!link || !id || !ids.includes(id)) return;
      setPinnedId(id);
      if (link.pathname === location.pathname) {
        document.getElementById(id)?.scrollIntoView();
      }
    };

    const onScrollEnd = () => {
      unpin();
      const id = detect();
      const hash = id ? `#${id}` : '';
      if (hash !== location.hash && (id || ids.includes(location.hash.slice(1)))) {
        history.replaceState(null, '', `${location.pathname}${location.search}${hash}`);
      }
    };

    onScroll();
    document.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scrollend', onScrollEnd);
    for (const type of TAKEOVER_EVENTS) window.addEventListener(type, unpin, { passive: true });
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', onScrollEnd);
      for (const type of TAKEOVER_EVENTS) window.removeEventListener(type, unpin);
    };
  }, [ids]);

  return pinnedId ?? scrolledId;
};
